import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Mail, User, Store, Smartphone, Shirt, Boxes, Cpu, CarFront, Building2, BriefcaseBusiness, Barcode, CalendarClock, Layers, Check, ArrowRight, ArrowLeft, LoaderCircle, AlertCircle, ShieldCheck } from 'lucide-react'
import { useDispatch } from 'react-redux'
import { useAuth } from '../../context/AuthContext'
import { useStore } from '../../context/StoreContext'
import { userData } from '../../store/actions'
import AuthShell from './AuthShell'
import { AuthInput, ContactMethod, PasswordField, PhoneField, authButton } from './AuthFields'
import BusinessLinePicker from '../../components/Onboarding/BusinessLinePicker'
import OtpVerificationModal from './OtpVerificationModal'

type StoreCategory =
  | "ELECTRONICS"
  | "GENERAL"
  | "FASHION"
  | "PHARMACY"
  | "GROCERY"
  | "AUTO_PARTS"
  | "CAR_BOUTIQUE"
  | "REAL_ESTATE"
  | "SERVICE_BUSINESS"

// ✅ legacy (pour ne pas casser ton backend actuel si tu utilises storeType)
type StoreTypeLegacy = "PHONE" | "CLOTHING" | "TECHNOLOGY" | StoreCategory

const STORE_CATEGORIES: Array<{
  key: StoreCategory
  label: string
  description: string
  icon: React.ReactNode
  available: boolean
  modules: Array<{ label: string; icon: React.ReactNode }>
  legacyType: StoreTypeLegacy
  comingSoonRoute?: string
}> = [
  {
    key: "ELECTRONICS",
    label: "Électronique",
    description: "Téléphones, ordinateurs, accessoires",
    icon: <Cpu />,
    available: true,
    modules: [
      { label: "IMEI / Séries", icon: <Smartphone size={14} /> },
      { label: "Code-barres", icon: <Barcode size={14} /> },
      { label: "Ventes & dettes", icon: <Boxes size={14} /> },
    ],
    legacyType: "TECHNOLOGY",
  },
  {
    key: "GENERAL",
    label: "Boutique générale",
    description: "Produits simples : stock & ventes",
    icon: <Store />,
    available: true,
    modules: [
      { label: "Code-barres", icon: <Barcode size={14} /> },
      { label: "Ventes & dettes", icon: <Boxes size={14} /> },
    ],
    legacyType: "GENERAL",
  },
  {
    key: "SERVICE_BUSINESS",
    label: "Business services",
    description: "Prestations, durees d'intervention, devis et demandes clients",
    icon: <BriefcaseBusiness />,
    available: true,
    modules: [
      { label: "Catalogue services", icon: <BriefcaseBusiness size={14} /> },
      { label: "Prix ou devis", icon: <Boxes size={14} /> },
      { label: "Demandes clients", icon: <CalendarClock size={14} /> },
    ],
    legacyType: "GENERAL",
  },
  {
    key: "CAR_BOUTIQUE",
    label: "Auto / Voitures",
    description: "Véhicules en vente ou location avec fiches détaillées",
    icon: <CarFront />,
    available: true,
    modules: [
      { label: "Catalogue véhicules", icon: <CarFront size={14} /> },
      { label: "Code-barres", icon: <Barcode size={14} /> },
      { label: "Ventes & locations", icon: <Boxes size={14} /> },
    ],
    legacyType: "GENERAL",
  },
  {
    key: "REAL_ESTATE",
    label: "Immobilier",
    description: "Résidences, appartements et biens meublés pour vente ou location",
    icon: <Building2 />,
    available: true,
    modules: [
      { label: "Catalogue immobilier", icon: <Building2 size={14} /> },
      { label: "Galerie images", icon: <Layers size={14} /> },
      { label: "Locations & ventes", icon: <CalendarClock size={14} /> },
    ],
    legacyType: "GENERAL",
  },
  {
    key: "FASHION",
    label: "Mode / Vêtements",
    description: "Tailles, couleurs, pointures et variantes",
    icon: <Shirt />,
    available: true,
    modules: [
      { label: "Variantes", icon: <Layers size={14} /> },
      { label: "Code-barres", icon: <Barcode size={14} /> },
    ],
    legacyType: "FASHION",
    comingSoonRoute: "/fashion-coming-soon",
  },
  // {
  //   key: "PHARMACY",
  //   label: "Pharmacie",
  //   description: "Lots & dates de péremption",
  //   icon: <Pill />,
  //   available: false,
  //   modules: [
  //     { label: "Péremption", icon: <CalendarClock size={14} /> },
  //     { label: "Code-barres", icon: <Barcode size={14} /> },
  //   ],
  //   legacyType: "PHARMACY",
  //   comingSoonRoute: "/coming-soon",
  // },
  // {
  //   key: "GROCERY",
  //   label: "Supermarché",
  //   description: "Produits, unités, code-barres",
  //   icon: <ShoppingBasket />,
  //   available: false,
  //   modules: [
  //     { label: "Code-barres", icon: <Barcode size={14} /> },
  //     { label: "Ventes", icon: <Boxes size={14} /> },
  //   ],
  //   legacyType: "GROCERY",
  //   comingSoonRoute: "/coming-soon",
  // },
  // {
  //   key: "AUTO_PARTS",
  //   label: "Pièces détachées",
  //   description: "Références & compatibilités",
  //   icon: <Wrench />,
  //   available: false,
  //   modules: [{ label: "Références", icon: <Wrench size={14} /> }],
  //   legacyType: "AUTO_PARTS",
  //   comingSoonRoute: "/coming-soon",
  // },
]

function resolveStoreCategoryFromQuery(value: string | null): StoreCategory | null {
  const normalized = String(value || "").trim().toLowerCase()

  if (!normalized) return null
  if (normalized === "auto" || normalized === "car" || normalized === "vehicle") {
    return "CAR_BOUTIQUE"
  }
  if (normalized === "real-estate" || normalized === "real_estate" || normalized === "property") {
    return "REAL_ESTATE"
  }
  if (normalized === "general") return "GENERAL"
  if (normalized === "service" || normalized === "services") return "SERVICE_BUSINESS"
  if (normalized === "electronics" || normalized === "tech") return "ELECTRONICS"
  if (normalized === "fashion") return "FASHION"

  return null
}

export default function Register() {
  const [query] = useSearchParams()
  const initialCategory = resolveStoreCategoryFromQuery(query.get('store')) || 'GENERAL'
  const { register,verifyUser,isAuthenticated } = useAuth()
  const { setCurrentStore } = useStore()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [step,setStep] = useState(0)
  const [method,setMethod] = useState<'email' | 'phone'>('email')
  const [form,setForm] = useState({ fullName:'',email:'',phone:'',storeName:'',password:'',confirm:'',storeCategory:initialCategory })
  const [countryCode,setCountryCode] = useState('+225')
  const [businessLines,setBusinessLines] = useState<string[]>(initialCategory === 'FASHION' ? ['FASHION_CLOTHING','FASHION_SHOES','FASHION_BAGS'] : ['GENERAL_RETAIL'])
  const [loading,setLoading] = useState(false)
  const [error,setError] = useState('')
  const [verification,setVerification] = useState<any>(null)
  const category = STORE_CATEGORIES.find(c => c.key === form.storeCategory) || STORE_CATEGORIES[0]
  const steps = ['Vos informations','Votre activité','Sécurité']
  useEffect(() => {
    const value = resolveStoreCategoryFromQuery(query.get('store'))
    if (value) { setForm(prev => ({ ...prev,storeCategory:value }));setBusinessLines(value === 'FASHION' ? ['FASHION_CLOTHING','FASHION_SHOES','FASHION_BAGS'] : ['GENERAL_RETAIL']) }
  },[query])
  function validate(index:number) {
    if (index === 0 && (!form.fullName.trim() || (method === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) || (method === 'phone' && !form.phone.replace(/\D/g,'')))) return 'Renseignez votre nom et un moyen de contact valide.'
    if (index === 1 && !form.storeName.trim()) return 'Renseignez le nom de votre activité.'
    if (index === 1 && !businessLines.length) return 'Choisissez au moins une famille de produits.'
    if (index === 2 && form.password.length < 6) return 'Choisissez un mot de passe d’au moins 6 caractères.'
    if (index === 2 && form.password !== form.confirm) return 'Les mots de passe ne correspondent pas.'
    return ''
  }
  const passwordScore = [form.password.length >= 8, /[a-z]/.test(form.password) && /[A-Z]/.test(form.password), /\d/.test(form.password), /[^a-zA-Z0-9]/.test(form.password)].filter(Boolean).length
  if (isAuthenticated) return <Navigate to="/dashboard" replace />
  return <AuthShell wide><div className="mb-7"><p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">Commencez avec GeStock</p><h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">Votre activité mérite un bel espace.</h1><p className="mt-3 text-sm leading-relaxed text-slate-500">Créez votre compte, personnalisez votre boutique et prenez les commandes.</p></div>
    <ol className="mb-8 grid grid-cols-3 gap-2" aria-label="Étapes de création">{steps.map((title,index) => <li key={title} aria-current={index === step ? 'step' : undefined} className={`border-b-2 pb-3 ${index <= step ? 'border-blue-500' : 'border-slate-100'}`}><span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold ${index < step ? 'bg-emerald-50 text-emerald-600' : index === step ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400'}`}>{index < step ? <Check size={12} /> : index+1}</span><span className={`ml-2 text-[11px] font-medium sm:text-xs ${index === step ? 'text-slate-800' : 'text-slate-400'}`}>{title}</span></li>)}</ol>
    <form className="space-y-5" onSubmit={async e => {
      e.preventDefault(); if (loading) return
      const currentError = validate(step);setError(currentError);if (currentError) return
      if (step < 2) { setStep(step+1);return }
      for (let index=0;index<3;index++) { const invalid = validate(index);if (invalid) { setStep(index);setError(invalid);return } }
      setLoading(true)
      const contact = method === 'email' ? form.email.trim().toLowerCase() : `${countryCode}${form.phone.replace(/\D/g,'')}`
      try {
        const result = await register({ fullName:form.fullName.trim(),password:form.password,storeName:form.storeName.trim(),storeCategory:form.storeCategory,storeType:category.legacyType,businessLines,...(method === 'email' ? { email:contact } : { phone:contact,countryCode }) })
        setVerification({ contact,userId:method === 'email' ? result.user?._id || result.user?.id : undefined,message:result.deliveryPending ? result.message : undefined })
      } catch (err:any) { setError(err.message || 'Impossible de créer le compte. Réessayez.') }
      finally { setLoading(false) }
    }}>
      {error && <div role="alert" className="flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 p-3 text-sm text-red-700"><AlertCircle size={17} className="shrink-0" />{error}</div>}
      <fieldset disabled={loading} className="space-y-5">
        {step === 0 && <><AuthInput label="Nom complet" name="fullName" icon={<User size={18} />} value={form.fullName} onChange={e => setForm({ ...form,fullName:e.target.value })} required autoComplete="name" placeholder="Votre prénom et nom" /><ContactMethod value={method} onChange={value => { setMethod(value);setError('') }} />{method === 'email' ? <AuthInput label="Adresse email" name="email" icon={<Mail size={18} />} type="email" required value={form.email} onChange={e => setForm({ ...form,email:e.target.value })} autoComplete="email" placeholder="vous@exemple.com" hint="Un code de vérification sera envoyé à cette adresse. Aucun téléphone n'est requis." /> : <><PhoneField phone={form.phone} countryCode={countryCode} onPhone={value => setForm({ ...form,phone:value })} onCountry={setCountryCode} /><p className="text-xs text-slate-400">Un code de vérification sera envoyé par téléphone.</p></>}</>}
        {step === 1 && <><AuthInput label="Nom de votre activité" name="storeName" icon={<Store size={18} />} required value={form.storeName} onChange={e => setForm({ ...form,storeName:e.target.value })} autoComplete="organization" placeholder="Ex. Boutique Plateau" /><div><p className="mb-3 text-sm font-medium text-slate-700">Quel type d'activité gérez-vous ?</p><div className="grid gap-2 sm:grid-cols-2">{STORE_CATEGORIES.map(c => <button type="button" aria-pressed={form.storeCategory === c.key} key={c.key} onClick={() => { setForm({ ...form,storeCategory:c.key });setBusinessLines(c.key === 'FASHION' ? ['FASHION_CLOTHING','FASHION_SHOES','FASHION_BAGS'] : ['GENERAL_RETAIL']) }} className={`flex items-center gap-3 rounded-xl border p-3.5 text-left transition ${form.storeCategory === c.key ? 'border-blue-500 bg-blue-50/60 ring-1 ring-blue-500' : 'border-slate-200 hover:border-blue-200'}`}><span className={`rounded-lg p-2 [&>svg]:h-4 [&>svg]:w-4 ${form.storeCategory === c.key ? 'bg-blue-100 text-blue-600' : 'bg-slate-50 text-slate-400'}`}>{c.icon}</span><span className="min-w-0 flex-1"><span className="block text-xs font-semibold text-slate-700">{c.label}</span><span className="mt-0.5 block text-[10px] leading-relaxed text-slate-400">{c.description}</span></span>{form.storeCategory === c.key && <Check size={14} className="shrink-0 text-blue-600" />}</button>)}</div></div>{['FASHION','GENERAL'].includes(form.storeCategory) && <div><p className="mb-3 text-sm font-medium text-slate-700">Que vendez-vous ?</p><BusinessLinePicker value={businessLines} onChange={setBusinessLines} /></div>}</>}
        {step === 2 && <><div className="flex gap-3 rounded-xl border border-blue-100 bg-blue-50/50 p-4"><ShieldCheck className="shrink-0 text-blue-500" size={22} /><div><p className="text-sm font-semibold text-slate-800">Une dernière étape pour {form.storeName}</p><p className="mt-1 text-xs leading-relaxed text-slate-500">Votre code de vérification sera envoyé à <strong>{method === 'email' ? form.email : `${countryCode}${form.phone}`}</strong>.</p></div></div><PasswordField name="password" label="Créer un mot de passe" required minLength={6} autoComplete="new-password" value={form.password} onChange={e => setForm({ ...form,password:e.target.value })} placeholder="Au moins 6 caractères" />{form.password && <div><div className="flex gap-1">{[1,2,3,4].map(n => <div key={n} className={`h-1 flex-1 rounded-full ${n <= passwordScore ? passwordScore < 2 ? 'bg-amber-400' : 'bg-emerald-400' : 'bg-slate-100'}`} />)}</div><p className="mt-2 text-[10px] text-slate-400">{passwordScore >= 3 ? 'Votre mot de passe est robuste.' : 'Pour plus de sécurité, combinez 8 caractères, lettres, chiffres et symboles.'}</p></div>}<PasswordField name="confirm" label="Confirmer le mot de passe" required minLength={6} autoComplete="new-password" value={form.confirm} onChange={e => setForm({ ...form,confirm:e.target.value })} placeholder="Saisissez à nouveau votre mot de passe" /><div className="grid grid-cols-2 gap-3 rounded-xl bg-slate-50 p-4 text-xs"><div><p className="text-slate-400">Administrateur</p><p className="mt-1 font-medium text-slate-700">{form.fullName}</p></div><div><p className="text-slate-400">Activité</p><p className="mt-1 font-medium text-slate-700">{category.label}</p></div></div></>}
      </fieldset>
      <div className="flex gap-3 border-t border-slate-100 pt-5">{step > 0 && <button type="button" disabled={loading} onClick={() => { setStep(step-1);setError('') }} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-600 disabled:opacity-50"><ArrowLeft size={16} />Retour</button>}<button disabled={loading} className={authButton}>{loading ? <><LoaderCircle size={16} className="animate-spin" />Création du compte…</> : step < 2 ? <>Continuer<ArrowRight size={16} /></> : <>Créer mon compte<ArrowRight size={16} /></>}</button></div>
    </form>
    <p className="mt-7 text-center text-sm text-slate-400">Déjà un compte ? <Link className="font-semibold text-blue-600 hover:underline" to="/login">Se connecter</Link></p>
    {verification && <OtpVerificationModal isOpen phone={verification.contact} emailUserId={verification.userId} deliveryMessage={verification.message} onClose={() => { setVerification(null);navigate('/login') }} onSuccess={result => { if (result.store) setCurrentStore(result.store);verifyUser(result.token,result.store);dispatch(userData());navigate('/dashboard',{ replace:true }) }} />}
  </AuthShell>
}
